import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useSupervisionReport,
    useExportSupervision,
    type SupervisionFilter,
} from '@/api/reports';
import { useUserDirectory } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/FormField';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
/**
 * Supervision Compliance — BACB monthly rule, per RBT. An RBT is compliant
 * for a month iff supervision hours ≥ 5% of direct hours AND ≥ 1 Individual
 * contact happened. Rows surface the failing months so a BCBA can drill in.
 */

export function SupervisionCompliancePage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));

    const [filter, setFilter] = useState<SupervisionFilter>({});
    const [draft, setDraft]   = useState<SupervisionFilter>({});

    const { data, isLoading, isError, error, isFetching } = useSupervisionReport(filter);
    const exportM = useExportSupervision();
    const staffQuery = useUserDirectory();

    const rbtOptions = useMemo(() => {
        if (!staffQuery.data) return [];
        return staffQuery.data
            .filter((s) => s.roleType === 'RBT')
            .map((s) => ({ id: s.id, label: `${s.last}, ${s.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [staffQuery.data]);

    const bcbaOptions = useMemo(() => {
        if (!staffQuery.data) return [];
        return staffQuery.data
            .filter((s) => s.roleType === 'BCBA')
            .map((s) => ({ id: s.id, label: `${s.last}, ${s.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [staffQuery.data]);

    function apply() { setFilter({ ...draft }); }
    function clear() { setDraft({}); setFilter({}); }

    async function runExport() {
        try { await exportM.mutateAsync(filter); } catch { /* isError renders below */ }
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                        <ArrowLeft className="w-3 h-3" /> All reports
                    </Link>
                    <h1 className="font-display text-2xl font-bold text-text">Supervision Compliance</h1>
                    <p className="text-xs text-text-subtle">
                        BACB monthly rule: supervision ≥ 5% of direct hours AND ≥ 1 Individual contact per active month.
                    </p>
                </div>
                {canExport && (
                    <Button variant="primary" size="sm" onClick={runExport} disabled={exportM.isPending || isLoading}>
                        {exportM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                )}
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                        <SelectField label="RBT" value={draft.rbtId} options={rbtOptions}
                            onChange={(v) => setDraft({ ...draft, rbtId: v })} />
                        <SelectField label="BCBA" value={draft.bcbaId} options={bcbaOptions}
                            onChange={(v) => setDraft({ ...draft, bcbaId: v })} />
                        <label className="text-xs block">
                            <span className="text-text-muted">Compliance</span>
                            <select
                                value={draft.compliance ?? ''}
                                onChange={(e) => setDraft({ ...draft, compliance: (e.target.value || undefined) as SupervisionFilter['compliance'] })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All RBTs</option>
                                <option value="compliant">Compliant only</option>
                                <option value="non_compliant">Non-compliant only</option>
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Date from</span>
                            <Input
                                type="date"
                                min="1900-01-01"
                                max={toIsoDate(new Date())}
                                value={draft.dateFrom ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Date to</span>
                            <Input
                                type="date"
                                min={draft.dateFrom || '1900-01-01'}
                                max={toIsoDate(new Date())}
                                value={draft.dateTo ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
                        <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
                        {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                    </div>
                </CardBody>
            </Card>

            {isError ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-error font-medium">Couldn't load report</p>
                        <p className="text-xs text-text-subtle mt-1">{extractErrorMessage(error, 'Please try again.')}</p>
                    </CardBody>
                </Card>
            ) : isLoading || !data ? (
                <Card><CardBody><div className="flex items-center gap-2 text-text-subtle text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></CardBody></Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        <Kpi label="RBTs" value={data.kpis.totalRbts.toLocaleString()} />
                        <Kpi label="Compliant" value={data.kpis.compliantRbts.toLocaleString()} />
                        <Kpi
                            label="Non-compliant"
                            value={data.kpis.nonCompliantRbts.toLocaleString()}
                            emphasis={data.kpis.nonCompliantRbts > 0}
                        />
                        <Kpi label="Direct hours" value={data.kpis.totalDirectHours.toLocaleString()} />
                        <Kpi label="Supervision hours" value={data.kpis.totalSupervisionHours.toLocaleString()} />
                        <Kpi
                            label={`Overall ratio (≥${data.kpis.ratioThresholdPct}%)`}
                            value={`${data.kpis.overallRatioPct.toFixed(1)}%`}
                            emphasis={data.kpis.overallRatioPct < data.kpis.ratioThresholdPct}
                        />
                    </div>

                    <Card>
                        <CardHeader><CardTitle>Per RBT</CardTitle></CardHeader>
                        <CardBody className="pt-0 overflow-x-auto">
                            {data.rbts.length === 0 ? (
                                <p className="text-xs text-text-subtle px-3 py-4">No RBTs match the current filter.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">RBT</th>
                                            <th className="px-3 py-2 font-bold text-right">Direct hrs</th>
                                            <th className="px-3 py-2 font-bold text-right">Sup hrs</th>
                                            <th className="px-3 py-2 font-bold text-right">Ratio</th>
                                            <th className="px-3 py-2 font-bold text-right">Individual</th>
                                            <th className="px-3 py-2 font-bold text-right">Months</th>
                                            <th className="px-3 py-2 font-bold">Failing months</th>
                                            <th className="px-3 py-2 font-bold">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.rbts.map((r) => (
                                            <tr key={r.staff_id ?? r.staff_name} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2">{r.staff_name}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.direct_hours}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.supervision_hours}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    <span className={r.ratio_pct < data.kpis.ratioThresholdPct ? 'text-warning-text font-medium' : ''}>
                                                        {r.ratio_pct.toFixed(1)}%
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.individual_contacts}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.months_in_service}</td>
                                                <td className="px-3 py-2 text-xs text-text-subtle">
                                                    {r.failing_months.length === 0 ? '—' : r.failing_months.join(', ')}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {r.compliant ? (
                                                        <span className="inline-flex items-center gap-1 text-success-text text-xs font-medium">
                                                            <ShieldCheck className="w-3.5 h-3.5" /> Compliant
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-warning-text text-xs font-medium">
                                                            <ShieldAlert className="w-3.5 h-3.5" /> Review
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>

                    {data.log.length > 0 && (
                        <Card>
                            <CardHeader><CardTitle>Recent supervision log</CardTitle></CardHeader>
                            <CardBody className="pt-0 overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Date</th>
                                            <th className="px-3 py-2 font-bold">RBT</th>
                                            <th className="px-3 py-2 font-bold">BCBA</th>
                                            <th className="px-3 py-2 font-bold">Method</th>
                                            <th className="px-3 py-2 font-bold text-right">Hours</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.log.map((e) => (
                                            <tr key={e.id} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2 tabular-nums">{e.date?.slice(0, 10)}</td>
                                                <td className="px-3 py-2">{e.rbt_name}</td>
                                                <td className="px-3 py-2">{e.bcba_name}</td>
                                                <td className="px-3 py-2 text-xs">{e.method || '—'}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{e.hours}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardBody>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
}

function SelectField({
    label, value, options, onChange,
}: {
    label: string;
    value: string | undefined;
    options: { id: string; label: string }[];
    onChange: (v: string | undefined) => void;
}) {
    return (
        <label className="text-xs block">
            <span className="text-text-muted">{label}</span>
            <select
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value || undefined)}
                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
            >
                <option value="">All</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
        </label>
    );
}

function Kpi({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
    return (
        <Card>
            <CardBody>
                <p className="text-2xs uppercase tracking-wider text-text-subtle">{label}</p>
                <p className={`font-display text-xl font-bold mt-1 tabular-nums ${emphasis ? 'text-warning-text' : 'text-text'}`}>{value}</p>
            </CardBody>
        </Card>
    );
}
